import React, { useState, useEffect } from 'react';
import { Product, User } from '../types';
import { productsAPI, usersAPI } from '../services/api';
import './Panel.css';

interface Category {
  id: number;
  name: string;
  order_requirements: string;
  available_until: string;
  show_order_banner: boolean;
  show_availability_banner: boolean;
  is_active: boolean;
  min_order_quantity: number;
  delivery_window: string;
  show_delivery_banner: boolean;
}

// Local edit buffer for the Quantities tab, so typing does not fire a request
// per keystroke and the admin can review before saving.
interface InventoryDraft {
  inventory_count: string;
  low_stock_threshold: string;
  track_inventory: boolean;
}

const AdminPanel: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'products' | 'users' | 'categories' | 'quantities'>('products');
  const [products, setProducts] = useState<Product[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState<Product | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [userSearch, setUserSearch] = useState<string>('');
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [deleteMessage, setDeleteMessage] = useState<string>('');
  const [settings, setSettings] = useState<{tax_rate: number, delivery_fee: number}>({tax_rate: 8.738, delivery_fee: 15.00});
  const [inventoryDrafts, setInventoryDrafts] = useState<{[id: number]: InventoryDraft}>({});
  const [savingInventory, setSavingInventory] = useState<number | null>(null);
  const [inventoryMessage, setInventoryMessage] = useState<{id: number, text: string, ok: boolean} | null>(null);

  const [newProduct, setNewProduct] = useState<Partial<Product>>(() => ({
    name: '',
    description: '',
    price: '0',
    category: categories.length > 0 ? categories[0].name : 'Mums',
    season: 'fall',
    min_order_quantity: 1,
    is_active: true,
    image_url: ''
  }));

  // Update newProduct category when categories are loaded
  useEffect(() => {
    if (categories.length > 0 && !newProduct.category) {
      setNewProduct(prev => ({ ...prev, category: categories[0].name }));
    }
  }, [categories, newProduct.category]);

  useEffect(() => {
    if (activeTab === 'products') {
      fetchProducts();
      fetchCategories(); // Always fetch categories for product dropdowns
    } else if (activeTab === 'users') {
      fetchUsers();
    } else if (activeTab === 'categories') {
      fetchCategories();
    } else if (activeTab === 'quantities') {
      fetchProducts();
    }
  }, [activeTab]);

  // Refresh the edit buffer whenever products are reloaded, so the inputs
  // always start from what is actually stored.
  useEffect(() => {
    setInventoryDrafts(
      products.reduce((acc, product) => {
        acc[product.id] = {
          inventory_count: String(product.inventory_count ?? 0),
          low_stock_threshold: String(product.low_stock_threshold ?? 25),
          track_inventory: product.track_inventory === true
        };
        return acc;
      }, {} as {[id: number]: InventoryDraft})
    );
  }, [products]);

  // Fetch categories on component mount for product forms
  useEffect(() => {
    fetchCategories();
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await fetch('/api/settings');
      const data = await response.json();
      setSettings(data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const updateSetting = async (key: string, value: number) => {
    try {
      await fetch(`/api/settings/${key}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ value })
      });
      fetchSettings();
    } catch (error) {
      console.error('Error updating setting:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await productsAPI.getAllAdmin();
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      console.log('Fetching users...');
      const response = await usersAPI.getAll();
      console.log('Users response:', response.data);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      alert('Error loading users: ' + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const compressImage = (file: File): Promise<string> => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d')!;
      const img = new Image();
      
      img.onload = () => {
        // Set max dimensions
        const maxWidth = 800;
        const maxHeight = 600;
        let { width, height } = img;
        
        // Calculate new dimensions
        if (width > height) {
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = (width * maxHeight) / height;
            height = maxHeight;
          }
        }
        
        canvas.width = width;
        canvas.height = height;
        
        // Draw and compress
        ctx.drawImage(img, 0, 0, width, height);
        const compressedDataUrl = canvas.toDataURL('image/jpeg', 0.8);
        resolve(compressedDataUrl);
      };
      
      img.src = URL.createObjectURL(file);
    });
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Check file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        alert('Image file size must be less than 5MB');
        return;
      }
      
      // Check file type
      if (!file.type.startsWith('image/')) {
        alert('Please select a valid image file');
        return;
      }
      
      setImageFile(file);
      
      try {
        const compressedImage = await compressImage(file);
        setImagePreview(compressedImage);
        if (editingProduct) {
          setEditingProduct({...editingProduct, image_url: compressedImage});
        }
      } catch (error) {
        console.error('Error compressing image:', error);
        alert('Error processing image');
      }
    }
  };

  const createProduct = async (productData: Partial<Product>) => {
    try {
      await productsAPI.create(productData);
      fetchProducts();
      setShowAddProduct(false);
      setImagePreview('');
      setImageFile(null);
      // Reset the form
      setNewProduct({
        name: '',
        description: '',
        price: '0',
        category: categories.length > 0 ? categories[0].name : 'Mums',
        season: 'fall',
        min_order_quantity: 1,
        is_active: true,
        image_url: ''
      });
    } catch (error) {
      console.error('Error creating product:', error);
      alert('Error creating product');
    }
  };

  const updateProduct = async (product: Product) => {
    try {
      await productsAPI.update(product.id, product);
      fetchProducts();
      setEditingProduct(null);
      setImagePreview('');
      setImageFile(null);
    } catch (error) {
      console.error('Error updating product:', error);
      alert('Error updating product');
    }
  };

  const deleteProduct = async (productId: number) => {
    try {
      await productsAPI.delete(productId);
      fetchProducts();
      setShowDeleteModal(null);
    } catch (error: any) {
      console.error('Error deleting product:', error);
      const errorMessage = error.response?.data?.message || 'Error deleting product';
      alert(errorMessage);
    }
  };

  const deactivateProduct = async (product: Product) => {
    try {
      await productsAPI.update(product.id, {...product, is_active: false});
      fetchProducts();
      setShowDeleteModal(null);
    } catch (error) {
      console.error('Error deactivating product:', error);
      alert('Error deactivating product');
    }
  };

  const updateInventoryDraft = (productId: number, changes: Partial<InventoryDraft>) => {
    setInventoryDrafts(prev => ({
      ...prev,
      [productId]: { ...prev[productId], ...changes }
    }));
  };

  const saveInventory = async (product: Product) => {
    const draft = inventoryDrafts[product.id];
    if (!draft) return;

    const count = parseInt(draft.inventory_count, 10);
    const threshold = parseInt(draft.low_stock_threshold, 10);

    if (!Number.isInteger(count) || count < 0) {
      setInventoryMessage({ id: product.id, text: 'Quantity must be 0 or more', ok: false });
      return;
    }
    if (!Number.isInteger(threshold) || threshold < 0) {
      setInventoryMessage({ id: product.id, text: 'Alert threshold must be 0 or more', ok: false });
      return;
    }

    setSavingInventory(product.id);
    setInventoryMessage(null);

    try {
      await productsAPI.updateInventory(product.id, {
        inventory_count: count,
        low_stock_threshold: threshold,
        track_inventory: draft.track_inventory
      });
      await fetchProducts();
      setInventoryMessage({ id: product.id, text: 'Saved', ok: true });
      setTimeout(() => setInventoryMessage(null), 3000);
    } catch (error: any) {
      setInventoryMessage({
        id: product.id,
        text: error.response?.data?.message || 'Could not save',
        ok: false
      });
    } finally {
      setSavingInventory(null);
    }
  };

  const isInventoryDirty = (product: Product) => {
    const draft = inventoryDrafts[product.id];
    if (!draft) return false;
    return (
      draft.inventory_count !== String(product.inventory_count ?? 0) ||
      draft.low_stock_threshold !== String(product.low_stock_threshold ?? 25) ||
      draft.track_inventory !== (product.track_inventory === true)
    );
  };

  const updateUserRole = async (userId: number, role: string) => {
    try {
      await usersAPI.updateRole(userId, role);
      fetchUsers();
    } catch (error) {
      console.error('Error updating user role:', error);
    }
  };

  const deleteUser = async (userId: number) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await usersAPI.delete(userId);
        fetchUsers();
      } catch (error) {
        console.error('Error deleting user:', error);
      }
    }
  };

  const fetchCategories = async () => {
    try {
      const endpoint = activeTab === 'categories' ? '/api/categories/admin' : '/api/categories';
      const response = await fetch(endpoint, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      setCategories(data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const createCategory = async (categoryData: Partial<Category>) => {
    try {
      await fetch('/api/categories', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(categoryData)
      });
      fetchCategories();
      setShowAddCategory(false);
    } catch (error) {
      console.error('Error creating category:', error);
    }
  };

  const updateCategory = async (category: Category) => {
    try {
      await fetch(`/api/categories/${category.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(category)
      });
      fetchCategories();
      fetchProducts(); // Refresh products to update category dropdown
      setEditingCategory(null);
    } catch (error) {
      console.error('Error updating category:', error);
    }
  };

  const deleteCategory = async (categoryId: number, categoryName: string) => {
    if (!window.confirm(`Are you sure you want to delete the "${categoryName}" category?`)) {
      return;
    }
    
    console.log('Attempting to delete category:', categoryId, categoryName);
    try {
      const token = localStorage.getItem('token');
      console.log('Using token:', token ? 'Token exists' : 'No token');
      
      const response = await fetch(`/api/categories/${categoryId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Delete response status:', response.status);
      const result = await response.json();
      console.log('Delete response:', result);
      
      if (response.ok) {
        console.log('Category deleted successfully, refreshing list...');
        await fetchCategories();
        setDeleteMessage('');
      } else {
        setDeleteMessage(result.message);
        setTimeout(() => setDeleteMessage(''), 5000);
      }
    } catch (error) {
      console.error('Error deleting category:', error);
      setDeleteMessage('Error deleting category');
      setTimeout(() => setDeleteMessage(''), 5000);
    }
  };

  const filteredUsers = users.filter(user => {
    const searchLower = userSearch.toLowerCase();
    return (
      (user.email || '').toLowerCase().includes(searchLower) ||
      (user.firstName || '').toLowerCase().includes(searchLower) ||
      (user.lastName || '').toLowerCase().includes(searchLower)
    );
  });

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="admin-panel">
      <div className="panel-header">
        <h1>Admin Panel</h1>
        <p>Manage your products and users!</p>
      </div>
      
      <div className="panel-tabs">
        <button 
          className={`tab-button ${activeTab === 'products' ? 'active' : ''}`}
          onClick={() => setActiveTab('products')}
        >
          Products
        </button>
        <button 
          className={`tab-button ${activeTab === 'quantities' ? 'active' : ''}`}
          onClick={() => setActiveTab('quantities')}
        >
          Quantities
        </button>
        <button 
          className={`tab-button ${activeTab === 'categories' ? 'active' : ''}`}
          onClick={() => setActiveTab('categories')}
        >
          Categories
        </button>
        <button 
          className={`tab-button ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          Users
        </button>
      </div>

      {activeTab === 'products' && (
        <div className="products-management">
          <div className="section-header">
            <h2>Product Management</h2>
            <button 
              className="add-product-btn"
              onClick={() => setShowAddProduct(true)}
            >
              + Add Product
            </button>
          </div>
          
          <div className="settings-section">
            <h3>Order Settings</h3>
            <div className="settings-grid">
              <div className="setting-item">
                <label>Tax Rate (%):</label>
                <input
                  type="number"
                  step="0.001"
                  value={settings.tax_rate}
                  onChange={(e) => updateSetting('tax_rate', parseFloat(e.target.value) || 0)}
                  className="setting-input"
                />
              </div>
              <div className="setting-item">
                <label>Under-Minimum Delivery Fee ($):</label>
                <input
                  type="number"
                  step="0.01"
                  value={settings.delivery_fee}
                  onChange={(e) => updateSetting('delivery_fee', parseFloat(e.target.value) || 0)}
                  className="setting-input"
                />
                <small className="setting-hint">
                  Charged only when an order misses every category minimum. Orders that
                  meet a minimum always get free home delivery. Set to 0 to make delivery
                  free for everyone.
                </small>
              </div>
            </div>
          </div>
          
          <div className="admin-products-grid">
            {products.map(product => (
              <div key={product.id} className="admin-product-card">
                {product.image_url && (
                  <div className="admin-product-image">
                    <img src={product.image_url} alt={product.name} />
                  </div>
                )}
                <div className="admin-product-info">
                  <h3>{product.name}</h3>
                  <p className="product-category">{product.category}</p>
                  <p className="product-price">${parseFloat(product.price).toFixed(2)}</p>
                  <p className="product-status">
                    Status: <span className={product.is_active ? 'active' : 'inactive'}>
                      {product.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </p>
                  <div className="admin-product-actions">
                    <button 
                      className="edit-btn"
                      onClick={() => setEditingProduct(product)}
                    >
                      Edit
                    </button>
                    <button 
                      className="delete-btn"
                      onClick={() => setShowDeleteModal(product)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'quantities' && (
        <div className="quantities-management">
          <div className="section-header">
            <h2>Quantity Management</h2>
          </div>

          <div className="quantities-intro">
            <p>
              Turn on <strong>Track quantity</strong> for a product to count it down as
              orders come in. Customers see "Only X left" once it reaches the alert
              threshold, and the product shows as <strong>OUT OF STOCK</strong> at zero
              without disappearing from the shop.
            </p>
            <p>
              When a product drops to or below its threshold, every admin account gets
              an email. Cancelling an order returns its items to the count automatically.
            </p>
            <p className="quantities-note">
              Products with tracking off are treated as unlimited, exactly as before.
            </p>
          </div>

          <div className="quantities-list">
            {products.map(product => {
              const draft = inventoryDrafts[product.id];
              if (!draft) return null;

              const stored = product.inventory_count ?? 0;
              const threshold = product.low_stock_threshold ?? 25;
              const tracking = product.track_inventory === true;
              const stockState = tracking
                ? stored <= 0 ? 'out' : stored <= threshold ? 'low' : 'ok'
                : 'untracked';
              const dirty = isInventoryDirty(product);
              const message = inventoryMessage?.id === product.id ? inventoryMessage : null;

              return (
                <div key={product.id} className={`quantity-card ${stockState}`}>
                  <div className="quantity-card-header">
                    <div className="quantity-product">
                      <h3>{product.name}</h3>
                      <span className="quantity-category">{product.category}</span>
                      {!product.is_active && (
                        <span className="quantity-badge inactive">Inactive</span>
                      )}
                    </div>
                    <div className="quantity-status">
                      {stockState === 'untracked' && (
                        <span className="quantity-badge untracked">Not tracked</span>
                      )}
                      {stockState === 'ok' && (
                        <span className="quantity-badge ok">{stored} in stock</span>
                      )}
                      {stockState === 'low' && (
                        <span className="quantity-badge low">Low: {stored} left</span>
                      )}
                      {stockState === 'out' && (
                        <span className="quantity-badge out">Out of stock</span>
                      )}
                    </div>
                  </div>

                  <div className="quantity-fields">
                    <div className="quantity-field">
                      <label htmlFor={`qty-${product.id}`}>Quantity on hand</label>
                      <input
                        id={`qty-${product.id}`}
                        type="number"
                        min="0"
                        step="1"
                        value={draft.inventory_count}
                        onChange={(e) =>
                          updateInventoryDraft(product.id, { inventory_count: e.target.value })
                        }
                      />
                    </div>

                    <div className="quantity-field">
                      <label htmlFor={`threshold-${product.id}`}>Alert below</label>
                      <input
                        id={`threshold-${product.id}`}
                        type="number"
                        min="0"
                        step="1"
                        value={draft.low_stock_threshold}
                        onChange={(e) =>
                          updateInventoryDraft(product.id, { low_stock_threshold: e.target.value })
                        }
                      />
                    </div>

                    <div className="quantity-field quantity-toggle">
                      <label className="checkbox-label" htmlFor={`track-${product.id}`}>
                        <input
                          id={`track-${product.id}`}
                          type="checkbox"
                          checked={draft.track_inventory}
                          onChange={(e) =>
                            updateInventoryDraft(product.id, { track_inventory: e.target.checked })
                          }
                        />
                        Track quantity
                      </label>
                    </div>

                    <div className="quantity-field quantity-save">
                      <button
                        className="save-btn"
                        onClick={() => saveInventory(product)}
                        disabled={savingInventory === product.id || !dirty}
                      >
                        {savingInventory === product.id ? 'Saving...' : dirty ? 'Save' : 'Saved'}
                      </button>
                    </div>
                  </div>

                  {message && (
                    <div className={`quantity-message ${message.ok ? 'ok' : 'error'}`}>
                      {message.text}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {products.length === 0 && (
            <div className="no-results">
              <h3>No products yet</h3>
              <p>Add products first, then set their quantities here.</p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'categories' && (
        <div className="categories-management">
          <div className="section-header">
            <h2>Category Management</h2>
            <button 
              className="add-product-btn"
              onClick={() => setShowAddCategory(true)}
            >
              + Add Category
            </button>
          </div>
          
          {deleteMessage && (
            <div className="error-message" style={{backgroundColor: '#ffebee', color: '#c62828', padding: '10px', borderRadius: '4px', marginBottom: '20px'}}>
              {deleteMessage}
            </div>
          )}
          
          <div className="categories-list">
            {categories.map(category => (
              <div key={category.id} className="category-card">
                <div className="category-info">
                  <h3>{category.name}</h3>
                  <p><strong>Order Requirements:</strong> {category.order_requirements || 'None'}</p>
                  <p><strong>Available Until:</strong> {category.available_until || 'No limit'}</p>
                  <p><strong>Minimum Order:</strong> {category.min_order_quantity || 1} items</p>
                  <p><strong>Delivery Window:</strong> {category.delivery_window || 'Not specified'}</p>
                  <div className="banner-status">
                    <span className={`status-badge ${category.show_order_banner ? 'active' : 'inactive'}`}>
                      Order Banner: {category.show_order_banner ? 'ON' : 'OFF'}
                    </span>
                    <span className={`status-badge ${category.show_availability_banner ? 'active' : 'inactive'}`}>
                      Availability Banner: {category.show_availability_banner ? 'ON' : 'OFF'}
                    </span>
                    <span className={`status-badge ${category.show_delivery_banner ? 'active' : 'inactive'}`}>
                      Delivery Banner: {category.show_delivery_banner ? 'ON' : 'OFF'}
                    </span>
                  </div>
                </div>
                
                <div className="category-controls">
                  <button 
                    className="edit-btn"
                    onClick={() => setEditingCategory(category)}
                  >
                    Edit
                  </button>
                  <button 
                    className="delete-btn"
                    onClick={() => deleteCategory(category.id, category.name)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'users' && (
        <div className="users-management">
          <div className="section-header">
            <h2>User Management</h2>
            <div className="search-container">
              <input
                type="text"
                placeholder="Search users by email or name..."
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                className="search-input"
              />
            </div>
          </div>
          
          <div className="users-list">
            {filteredUsers.map(user => (
              <div key={user.id} className="user-card">
                <div className="user-info">
                  <h3>{user.firstName} {user.lastName}</h3>
                  <p>{user.email}</p>
                  <p>Current Role: {user.role}</p>
                </div>
                
                <div className="user-controls">
                  <select 
                    value={user.role}
                    onChange={(e) => updateUserRole(user.id, e.target.value)}
                  >
                    <option value="customer">Customer</option>
                    <option value="associate">Associate</option>
                    <option value="admin">Admin</option>
                  </select>
                  <button 
                    onClick={() => deleteUser(user.id)}
                    className="delete-button"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add Product Modal */}
      {showAddProduct && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Add New Product</h3>
              <button 
                className="close-btn"
                onClick={() => {
                  setShowAddProduct(false);
                  setImagePreview('');
                  setImageFile(null);
                }}
              >
                ×
              </button>
            </div>
            <div className="modal-content">
              <div className="form-group">
                <label>Product Name</label>
                <input
                  type="text"
                  placeholder="Enter product name"
                  value={newProduct.name || ''}
                  onChange={(e) => setNewProduct({...newProduct, name: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  placeholder="Enter product description"
                  value={newProduct.description || ''}
                  onChange={(e) => setNewProduct({...newProduct, description: e.target.value})}
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Price ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={newProduct.price || '0'}
                    onChange={(e) => setNewProduct({...newProduct, price: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Category</label>
                  <select 
                    value={newProduct.category || (categories.length > 0 ? categories[0].name : '')}
                    onChange={(e) => setNewProduct({...newProduct, category: e.target.value})}
                  >
                    {categories.map(category => (
                      <option key={category.id} value={category.name}>{category.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>Product Image</label>
                <div className="image-upload">
                  <input
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    onChange={handleImageUpload}
                    id="image-upload"
                  />
                  <label htmlFor="image-upload" className="upload-btn">
                    Choose Image
                  </label>
                  {imagePreview && (
                    <div className="image-preview">
                      <img src={imagePreview} alt="Preview" />
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-actions">
                <button 
                  className="cancel-btn"
                  onClick={() => {
                    setShowAddProduct(false);
                    setImagePreview('');
                    setImageFile(null);
                    // Reset the form
                    setNewProduct({
                      name: '',
                      description: '',
                      price: '0',
                      category: categories.length > 0 ? categories[0].name : 'Mums',
                      season: 'fall',
                      min_order_quantity: 1,
                      is_active: true,
                      image_url: ''
                    });
                  }}
                >
                  Cancel
                </button>
                <button 
                  className="save-btn"
                  onClick={() => {
                    const productToCreate = { ...newProduct };
                    if (imagePreview) productToCreate.image_url = imagePreview;
                    createProduct(productToCreate);
                  }}
                >
                  Add Product
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Product Modal */}
      {editingProduct && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Edit Product</h3>
              <button 
                className="close-btn"
                onClick={() => {
                  setEditingProduct(null);
                  setImagePreview('');
                  setImageFile(null);
                }}
              >
                ×
              </button>
            </div>
            <div className="modal-content">
              <div className="form-group">
                <label>Product Name</label>
                <input
                  type="text"
                  value={editingProduct.name}
                  onChange={(e) => setEditingProduct({...editingProduct, name: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={editingProduct.description}
                  onChange={(e) => setEditingProduct({...editingProduct, description: e.target.value})}
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Price ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editingProduct.price}
                    onChange={(e) => setEditingProduct({...editingProduct, price: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Category</label>
                  <select
                    value={editingProduct.category}
                    onChange={(e) => setEditingProduct({...editingProduct, category: e.target.value})}
                  >
                    {categories.map(category => (
                      <option key={category.id} value={category.name}>{category.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>Product Image</label>
                <div className="image-upload">
                  <input
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    onChange={handleImageUpload}
                    id="edit-image-upload"
                  />
                  <label htmlFor="edit-image-upload" className="upload-btn">
                    Change Image
                  </label>
                  {(imagePreview || editingProduct.image_url) && (
                    <div className="image-preview">
                      <img src={imagePreview || editingProduct.image_url} alt="Preview" />
                    </div>
                  )}
                </div>
              </div>
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={editingProduct.is_active}
                    onChange={(e) => setEditingProduct({...editingProduct, is_active: e.target.checked})}
                  />
                  Active Product
                </label>
              </div>
              <div className="modal-actions">
                <button 
                  className="cancel-btn"
                  onClick={() => {
                    setEditingProduct(null);
                    setImagePreview('');
                    setImageFile(null);
                  }}
                >
                  Cancel
                </button>
                <button 
                  className="save-btn"
                  onClick={() => updateProduct(editingProduct)}
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="modal delete-modal">
            <div className="modal-header">
              <h3>Delete Product</h3>
              <button 
                className="close-btn"
                onClick={() => setShowDeleteModal(null)}
              >
                ×
              </button>
            </div>
            <div className="modal-content">
              <p>What would you like to do with "{showDeleteModal.name}"?</p>
              <div className="delete-options">
                <button 
                  className="deactivate-btn"
                  onClick={() => deactivateProduct(showDeleteModal)}
                >
                  Deactivate Temporarily
                </button>
                <button 
                  className="permanent-delete-btn"
                  onClick={() => deleteProduct(showDeleteModal.id)}
                >
                  Delete Permanently
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Category Modal */}
      {showAddCategory && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Add New Category</h3>
              <button className="close-btn" onClick={() => setShowAddCategory(false)}>×</button>
            </div>
            <div className="modal-content">
              <form onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.target as HTMLFormElement);
                createCategory({
                  name: formData.get('name') as string,
                  order_requirements: formData.get('order_requirements') as string,
                  available_until: formData.get('available_until') as string,
                  show_order_banner: formData.get('show_order_banner') === 'on',
                  show_availability_banner: formData.get('show_availability_banner') === 'on',
                  min_order_quantity: parseInt(formData.get('min_order_quantity') as string) || 1,
                  delivery_window: formData.get('delivery_window') as string,
                  show_delivery_banner: formData.get('show_delivery_banner') === 'on'
                });
              }}>
                <div className="form-group">
                  <label>Category Name</label>
                  <input type="text" name="name" required />
                </div>
                <div className="form-group">
                  <label>Order Requirements</label>
                  <textarea name="order_requirements" placeholder="e.g., Minimum 4 items required" />
                </div>
                <div className="form-group">
                  <label>Available Until</label>
                  <input type="text" name="available_until" placeholder="e.g., December 31st" />
                </div>
                <div className="form-group">
                  <label>Minimum Order Quantity</label>
                  <input type="number" name="min_order_quantity" min="1" defaultValue="1" />
                </div>
                <div className="form-group">
                  <label>Delivery Window</label>
                  <input type="text" name="delivery_window" placeholder="e.g., September 15-30" />
                </div>
                <div className="form-group">
                  <label className="checkbox-label">
                    <input type="checkbox" name="show_order_banner" />
                    Show Order Requirements Banner
                  </label>
                </div>
                <div className="form-group">
                  <label className="checkbox-label">
                    <input type="checkbox" name="show_availability_banner" />
                    Show Availability Banner
                  </label>
                </div>
                <div className="form-group">
                  <label className="checkbox-label">
                    <input type="checkbox" name="show_delivery_banner" />
                    Show Delivery Window Banner
                  </label>
                </div>
                <div className="modal-actions">
                  <button type="button" className="cancel-btn" onClick={() => setShowAddCategory(false)}>Cancel</button>
                  <button type="submit" className="save-btn">Add Category</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Category Modal */}
      {editingCategory && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Edit Category</h3>
              <button className="close-btn" onClick={() => setEditingCategory(null)}>×</button>
            </div>
            <div className="modal-content">
              <div className="form-group">
                <label>Category Name</label>
                <input
                  type="text"
                  value={editingCategory.name}
                  onChange={(e) => setEditingCategory({...editingCategory, name: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label>Order Requirements</label>
                <textarea
                  value={editingCategory.order_requirements}
                  onChange={(e) => setEditingCategory({...editingCategory, order_requirements: e.target.value})}
                  placeholder="e.g., Minimum 4 items required"
                />
              </div>
              <div className="form-group">
                <label>Available Until</label>
                <input
                  type="text"
                  value={editingCategory.available_until}
                  onChange={(e) => setEditingCategory({...editingCategory, available_until: e.target.value})}
                  placeholder="e.g., December 31st"
                />
              </div>
              <div className="form-group">
                <label>Minimum Order Quantity</label>
                <input
                  type="number"
                  min="1"
                  value={editingCategory.min_order_quantity || 1}
                  onChange={(e) => setEditingCategory({...editingCategory, min_order_quantity: parseInt(e.target.value) || 1})}
                />
              </div>
              <div className="form-group">
                <label>Delivery Window</label>
                <input
                  type="text"
                  value={editingCategory.delivery_window || ''}
                  onChange={(e) => setEditingCategory({...editingCategory, delivery_window: e.target.value})}
                  placeholder="e.g., September 15-30"
                />
              </div>
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={editingCategory.show_order_banner}
                    onChange={(e) => setEditingCategory({...editingCategory, show_order_banner: e.target.checked})}
                  />
                  Show Order Requirements Banner
                </label>
              </div>
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={editingCategory.show_availability_banner}
                    onChange={(e) => setEditingCategory({...editingCategory, show_availability_banner: e.target.checked})}
                  />
                  Show Availability Banner
                </label>
              </div>
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={editingCategory.show_delivery_banner}
                    onChange={(e) => setEditingCategory({...editingCategory, show_delivery_banner: e.target.checked})}
                  />
                  Show Delivery Window Banner
                </label>
              </div>
              <div className="modal-actions">
                <button className="cancel-btn" onClick={() => setEditingCategory(null)}>Cancel</button>
                <button className="save-btn" onClick={() => updateCategory(editingCategory)}>Save Changes</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;